import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Typography,
  IconButton,
  Box,
  Backdrop,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import io from "socket.io-client";
import { API_BASE_URL, SOCKET_URL } from "../config";

// Erstes Gebot mindestens 60€, danach aktuelles Gebot + 10€
const getMinBid = (trikot) => {
  const current = trikot.currentBid ?? 0;
  const starting = trikot.startingBid ?? 60;
  const hasNoBids = current === 0 || current === starting;
  return hasNoBids ? 60 : current + 10;
};

const BidDialog = ({ trikot, open, onClose, onBidSuccess }) => {
  const { user } = useAuth();
  const minBid = getMinBid(trikot);
  const [bidAmount, setBidAmount] = useState(String(minBid));
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [socket, setSocket] = useState(null);

  // Initialize socket when the component mounts and user is authenticated
  useEffect(() => {
    if (user) {
      const newSocket = io(SOCKET_URL);
      setSocket(newSocket);

      return () => {
        if (newSocket) {
          newSocket.disconnect();
        }
      };
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      onClose();
    }
  }, [user, onClose]);

  useEffect(() => {
    const checkExpiration = () => {
      if (new Date(trikot.endTime) < new Date()) {
        setError("Diese Auktion ist bereits beendet.");
        setTimeout(() => {
          onClose();
        }, 2000); // Close dialog after 2 seconds
      }
    };

    checkExpiration();
    const timer = setInterval(checkExpiration, 1000); // Check every second

    return () => clearInterval(timer);
  }, [trikot.endTime, onClose]);

  const handleBidSubmit = async () => {
    if (new Date(trikot.endTime) < new Date()) {
      setError("Diese Auktion ist bereits beendet.");
      setTimeout(() => {
        onClose();
      }, 2000);
      return;
    }

    const bidValue = parseFloat(bidAmount);
    if (isNaN(bidValue) || bidValue < minBid) {
      setError(
        minBid === 60
          ? "Das erste Gebot muss mindestens 60 € betragen."
          : `Das Gebot muss mindestens ${minBid} € betragen (10 € über aktuellem Gebot).`
      );
      return;
    }

    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_BASE_URL}/api/bids`,
        {
          trikotId: trikot._id,
          amount: bidValue,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Handle the response which now includes the possibly extended endTime
      if (socket) {
        socket.emit("newBid", {
          trikotId: trikot._id,
          amount: bidValue,
          userId: user._id,
          endTime: response.data.endTime, // Include new end time
        });
      }

      // Call onBidSuccess with the updated bid data including endTime
      if (onBidSuccess) {
        onBidSuccess({
          trikotId: trikot._id,
          amount: bidValue,
          endTime: response.data.endTime, // Include new end time
        });
      }

      onClose();
    } catch (error) {
      console.error("Bid error:", error);
      setError(
        error.response?.data?.message ||
          "Fehler beim Abgeben des Gebots. Bitte versuchen Sie es später erneut."
      );
    } finally {
      setLoading(false);
    }
  };

  // Update bidAmount when trikot changes
  useEffect(() => {
    if (trikot) {
      setBidAmount(String(getMinBid(trikot)));
    }
  }, [trikot]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  };

  const handleIncrease = () => {
    const currentValue = parseFloat(bidAmount) || minBid;
    const newValue = currentValue + 10;
    setBidAmount(String(newValue));
    setError(""); // Clear any previous errors
  };

  const handleDecrease = () => {
    const currentValue = parseFloat(bidAmount) || minBid;
    const newValue = Math.max(currentValue - 10, minBid);
    setBidAmount(String(newValue));
    setError(""); // Clear any previous errors
  };

  const canDecrease = () => {
    const currentValue = parseFloat(bidAmount);
    if (isNaN(currentValue)) return false;
    return currentValue > minBid;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      BackdropComponent={Backdrop}
      BackdropProps={{
        sx: {
          backdropFilter: "blur(4px)",
          backgroundColor: "rgba(0, 0, 0, 0.3)",
        },
      }}
    >
      <DialogTitle>
        Gebot abgeben für #{trikot.number} {trikot.playerName}
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Aktuelles Gebot: {formatCurrency(trikot.currentBid)}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Mindestgebot: {formatCurrency(minBid)}
        </Typography>

        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1 }}>
          <TextField
            margin="dense"
            label="Ihr Gebot (€)"
            type="number"
            fullWidth
            value={bidAmount}
            onChange={(e) => {
              const value = e.target.value;
              // Erlaube leeres Feld oder gültige Zahlen
              if (value === "" || !isNaN(parseFloat(value))) {
                setBidAmount(value);
              }
            }}
            inputProps={{
              min: minBid,
              step: 10,
            }}
            disabled={loading}
            helperText={
              minBid === 60
                ? "Startgebot mindestens 60 €"
                : `Mindestens ${formatCurrency(minBid)} (10 € über aktuellem Gebot)`
            }
            sx={{ flexGrow: 1 }}
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              mt: 1,
            }}
          >
            <IconButton
              onClick={handleIncrease}
              disabled={loading}
              color="primary"
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 0,
                minWidth: 48,
                minHeight: 48,
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              }}
              aria-label="Gebot um 10€ erhöhen"
            >
              <ArrowUpwardIcon />
            </IconButton>
            <IconButton
              onClick={handleDecrease}
              disabled={loading || !canDecrease()}
              color="primary"
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 0,
                minWidth: 48,
                minHeight: 48,
                "&:hover": {
                  backgroundColor: "action.hover",
                },
                "&:disabled": {
                  opacity: 0.5,
                },
              }}
              aria-label="Gebot um 10€ verringern"
            >
              <ArrowDownwardIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Abbrechen
        </Button>
        <Button
          onClick={handleBidSubmit}
          variant="contained"
          color="secondary" // Change this from primary to secondary
          disabled={
            loading ||
            !bidAmount ||
            isNaN(parseFloat(bidAmount)) ||
            parseFloat(bidAmount) < minBid ||
            !user
          }
        >
          {loading ? "Wird verarbeitet..." : "Gebot abgeben"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BidDialog;
